import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ColorChipButton,
  NextButton,
  ColorTextWrapper,
  ColorChipText,
} from './styles/ColorSwatches.style';
import { getIconPath, getViewportInfo } from '../../../../../utils';
import {
  PLP_COLOR_SWATCH_SCROLL_CONFIG,
  NEW_PLP_COLOR_SWATCH_SCROLL_CONFIG,
  PLP_SLP_COLOR_SWATCH_DIMENSIONS,
} from '../plpTileConstants';
import getColorSwatchList from './plpTileHelpers/colorSwatchHelper';
import BodyCopy from '../../../../common/atoms/BodyCopy';

const renderTextOrColorSwatches = ({
  isTileRedesignAB,
  colorSwatchList,
  productColorListRef,
  numberOfSwatchesToDisplay,
  clickHandler,
  scrollToNextColorSwatch,
  arrowRight,
  shouldScroll,
  isDynamicColorSwatchesEnabled,
  handleMouseEnter,
  handleMouseLeave,
  isHoverToPreviewColorSwatchesEnabled,
}) => {
  if (isTileRedesignAB === 'variationB') {
    return (
      <ColorTextWrapper>
        <BodyCopy className="color-text" fontFamily="secondary" fontSize="fs12">
          {`${colorSwatchList?.length || 1} Colors`}
        </BodyCopy>
      </ColorTextWrapper>
    );
  }
  if (!isTileRedesignAB && colorSwatchList?.length <= 1) {
    return <ColorTextWrapper isEmpty>{''}</ColorTextWrapper>;
  }
  let list = colorSwatchList;
  const { isDesktop } = getViewportInfo() || {};
  const shouldAddHoverProps = isDesktop && colorSwatchList?.length > 1;
  const maxSwatchLength = isDynamicColorSwatchesEnabled ? numberOfSwatchesToDisplay || 6 : 4;
  if (isTileRedesignAB && isTileRedesignAB === 'variationA')
    list = colorSwatchList.filter((val, idx) => idx < maxSwatchLength);
  return (
    <>
      <div className="flex-row justify-between color-swatch-container">
        <>
          <ol className="flex-row color-swatch-list" ref={productColorListRef}>
            {list?.map((colorSwatch) => {
              const extraSwatchProps =
                isHoverToPreviewColorSwatchesEnabled && shouldAddHoverProps
                  ? {
                      onMouseEnter: () => handleMouseEnter(colorSwatch.id),
                      onMouseLeave: handleMouseLeave,
                    }
                  : {};

              return (
                <ColorChipButton
                  key={colorSwatch.id}
                  type="button"
                  title={colorSwatch.name}
                  isActive={colorSwatch.isActive}
                  name={colorSwatch.name}
                  imageUrl={colorSwatch.imageUrl}
                  onClick={() => clickHandler(colorSwatch.id)}
                  isTileRedesignAB={isTileRedesignAB}
                  isDynamicColorSwatchesEnabled={isDynamicColorSwatchesEnabled}
                  {...extraSwatchProps}
                />
              );
            })}
            {isTileRedesignAB &&
              isTileRedesignAB === 'variationA' &&
              colorSwatchList.length > maxSwatchLength && (
                <ColorChipText
                  onClick={() => clickHandler(colorSwatchList[0]?.id)}
                  isDynamicColorSwatchesEnabled={isDynamicColorSwatchesEnabled}
                  aria-label={`${colorSwatchList.length - maxSwatchLength} additional colors`}
                >
                  <BodyCopy
                    className="color-text"
                    fontFamily="secondary"
                    fontSize="fs12"
                    component="div"
                  >
                    {`+${colorSwatchList?.length - maxSwatchLength}`}
                  </BodyCopy>
                </ColorChipText>
              )}
          </ol>
          {!isTileRedesignAB && (
            <NextButton
              onClick={scrollToNextColorSwatch}
              type="button"
              arrowRight={arrowRight}
              data-locator="color_swatch_arrow"
              shouldScroll={shouldScroll}
            />
          )}
        </>
      </div>
    </>
  );
};

const setScrollConfig = (swatchType) => {
  return swatchType
    ? { ...NEW_PLP_COLOR_SWATCH_SCROLL_CONFIG }
    : { ...PLP_COLOR_SWATCH_SCROLL_CONFIG };
};

const ColorSwatches = ({
  onQuickViewOpenClick,
  saveDataForQv,
  productPartNo,
  TCPSwatches,
  assetHost,
  assetPath,
  swatchImg,
  relatedSwatchImages,
  tcpColor,
  auxDescription,
  isBundleProduct,
  handleViewBundleClick,
  isNewSwatchUrlEnabled,
  isTileRedesignAB,
  isSLPTile,
  isDynamicColorSwatchesEnabled,
  handleMouseEnter,
  handleMouseLeave,
  isHoverToPreviewColorSwatchesEnabled,
  productStyleType,
  isSwatchAsColorUrlEnabled,
}) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  const [numberOfSwatchesToDisplay, setNumberOfSwatchesToDisplay] = useState(0);
  const arrowRight = getIconPath('icon-carrot-black-small');
  const productColorListRef = useRef(null);
  let scrollConfig = setScrollConfig(isTileRedesignAB)?.desktop;
  const { colorSwatchList } = getColorSwatchList({
    productPartNo,
    TCPSwatches,
    assetHost,
    assetPath,
    swatchImg,
    relatedSwatchImages,
    tcpColor,
    auxDescription,
    isNewSwatchUrlEnabled,
    productStyleType,
    isSwatchAsColorUrlEnabled,
  });

  useEffect(() => {
    const { isTablet, isMobile } = getViewportInfo();
    const config = setScrollConfig(isTileRedesignAB);
    if (isTablet) {
      scrollConfig = { ...config.tablet };
      if (
        colorSwatchList.length * scrollConfig.scrollWidth >
        productColorListRef?.current?.clientWidth
      ) {
        setShouldScroll(true);
      }
    } else if (isMobile) {
      scrollConfig = { ...config.mobile };
      if (
        colorSwatchList.length * scrollConfig.scrollWidth >
        productColorListRef?.current?.clientWidth
      ) {
        setShouldScroll(true);
      }
    } else {
      scrollConfig = { ...config.desktop };
      if (
        colorSwatchList.length * scrollConfig.scrollWidth >
        productColorListRef?.current?.clientWidth
      ) {
        setShouldScroll(true);
      }
    }
  }, [colorSwatchList, isTileRedesignAB]);

  useEffect(() => {
    if (isDynamicColorSwatchesEnabled) {
      const { mobile, tablet, desktop, remainingSwatchesTextWidth } =
        PLP_SLP_COLOR_SWATCH_DIMENSIONS;
      const { isTablet, isMobile } = getViewportInfo();

      const colorListWidth = productColorListRef?.current?.clientWidth;
      let colorSwatchWidth = desktop.width + desktop.marginRight;

      if (isTablet) {
        colorSwatchWidth = tablet.width + tablet.marginRight;
      }

      if (isMobile) {
        colorSwatchWidth = mobile.width + mobile.marginRight;
      }

      if (colorSwatchList.length * colorSwatchWidth > colorListWidth) {
        const maxSwatches = Math.floor(
          (colorListWidth - remainingSwatchesTextWidth) / colorSwatchWidth
        );

        setNumberOfSwatchesToDisplay(maxSwatches);
      } else {
        setNumberOfSwatchesToDisplay(colorSwatchList.length);
      }
    }
  }, [colorSwatchList]);

  const clickHandler = (swatchId) => {
    requestAnimationFrame(() => {
      if (isBundleProduct) {
        handleViewBundleClick();
        return;
      }
      saveDataForQv(swatchId);
      onQuickViewOpenClick({
        colorProductId: swatchId,
        fromPage: isSLPTile ? 'SLP' : 'PLP',
      });
    });
  };

  const scrollToNextColorSwatch = () => {
    if (productColorListRef) {
      const maxScrollLeft =
        colorSwatchList.length * scrollConfig.scrollWidth -
        productColorListRef?.current?.clientWidth;
      productColorListRef.current.scrollLeft += scrollConfig.scrollWidth;
      if (Math.ceil(productColorListRef.current.scrollLeft) >= maxScrollLeft) {
        productColorListRef.current.scrollLeft = 0;
      }
    }
  };

  return renderTextOrColorSwatches({
    isTileRedesignAB,
    colorSwatchList,
    productColorListRef,
    numberOfSwatchesToDisplay,
    clickHandler,
    scrollToNextColorSwatch,
    arrowRight,
    shouldScroll,
    isDynamicColorSwatchesEnabled,
    isHoverToPreviewColorSwatchesEnabled,
    handleMouseEnter,
    handleMouseLeave,
  });
};
ColorSwatches.propTypes = {
  onQuickViewOpenClick: PropTypes.func,
  saveDataForQv: PropTypes.func,
  handleMouseEnter: PropTypes.func,
  handleMouseLeave: PropTypes.func,
  productPartNo: PropTypes.string,
  TCPSwatches: PropTypes.string,
  assetHost: PropTypes.string.isRequired,
  assetPath: PropTypes.string.isRequired,
  swatchImg: PropTypes.string,
  tcpColor: PropTypes.string,
  auxDescription: PropTypes.string,
  relatedSwatchImages: PropTypes.array,
  isNewSwatchUrlEnabled: PropTypes.bool,
  isSLPTile: PropTypes.bool,
  isDynamicColorSwatchesEnabled: PropTypes.bool,
  isHoverToPreviewColorSwatchesEnabled: PropTypes.bool,
};
ColorSwatches.defaultProps = {
  onQuickViewOpenClick: () => {},
  saveDataForQv: () => {},
  handleMouseEnter: () => {},
  handleMouseLeave: () => {},
  productPartNo: '',
  TCPSwatches: '',
  swatchImg: '',
  tcpColor: '',
  auxDescription: '',
  relatedSwatchImages: [],
  isNewSwatchUrlEnabled: false,
  isSLPTile: false,
  isDynamicColorSwatchesEnabled: false,
  isHoverToPreviewColorSwatchesEnabled: false,
};
export default ColorSwatches;
